import { useCallback, useState, useEffect } from 'react';
import { Block } from '../types/shared/blocks';
import { QuoteState } from '../types/stateProviders/QuoteStateProvider';
import { Quotation } from '../types/api/Quotation';
import { saveWithQuotaManagement } from '../utils/localStorageCleanup';
import removeBlockRefs from '../utils/removeBlockRefs';

interface QuoteDraft {
	quoteId: string;
	blocks: Block[];
	quoteState: Partial<QuoteState>;
	lastModified: string;
	lastSyncedToServer: string | null;
	serverLastSavedAt: string | null;
	isDirty: boolean;
	version: number;
}

interface UseLocalStorageSyncReturn {
	saveLocal: (blocks: Block[], quoteState: Partial<QuoteState>) => void;
	getLocalDraft: () => QuoteDraft | null;
	clearLocal: () => void;
	isDirty: boolean;
	lastModified: string | null;
	reconcile: (serverData: Quotation) => 'use_local' | 'use_server' | 'conflict';
	markSynced: (serverTimestamp: string) => void;
}

export const useLocalStorageSync = (
	quoteId: string | undefined
): UseLocalStorageSyncReturn => {
	const [isDirty, setIsDirty] = useState(false);
	const [lastModified, setLastModified] = useState<string | null>(null);

	const getStorageKey = useCallback(() => {
		return `quote_${quoteId}_draft`;
	}, [quoteId]);

	const getLocalDraft = useCallback((): QuoteDraft | null => {
		if (!quoteId) return null;

		try {
			const stored = localStorage.getItem(getStorageKey());
			if (!stored) return null;

			const draft: QuoteDraft = JSON.parse(stored);
			return draft;
		} catch (error) {
			return null;
		}
	}, [quoteId, getStorageKey]);

	const saveLocal = useCallback(
		(blocks: Block[], quoteState: Partial<QuoteState>) => {
			if (!quoteId) return;

			const existingDraft = getLocalDraft();
			const now = new Date().toISOString();

			const blocksWithoutRefs = removeBlockRefs(blocks);

			const draft: QuoteDraft = {
				quoteId,
				blocks: blocksWithoutRefs,
				quoteState,
				lastModified: now,
				lastSyncedToServer: existingDraft?.lastSyncedToServer || null,
				serverLastSavedAt: existingDraft?.serverLastSavedAt || null,
				isDirty: true,
				version: (existingDraft?.version || 0) + 1,
			};

			const success = saveWithQuotaManagement(getStorageKey(), draft, quoteId);

			if (success) {
				setIsDirty(true);
				setLastModified(now);
			} else {
			}
		},
		[quoteId, getStorageKey, getLocalDraft]
	);

	const clearLocal = useCallback(() => {
		if (!quoteId) return;

		try {
			localStorage.removeItem(getStorageKey());
			setIsDirty(false);
			setLastModified(null);
		} catch (error) {}
	}, [quoteId, getStorageKey]);

	const markSynced = useCallback(
		(serverTimestamp: string) => {
			if (!quoteId) return;

			try {
				const draft = getLocalDraft();
				if (!draft) return;

				draft.isDirty = false;
				draft.lastSyncedToServer = new Date().toISOString();
				draft.serverLastSavedAt = serverTimestamp;

				localStorage.setItem(getStorageKey(), JSON.stringify(draft));
				setIsDirty(false);
			} catch (error) {}
		},
		[quoteId, getStorageKey, getLocalDraft]
	);

	const reconcile = useCallback(
		(serverData: Quotation): 'use_local' | 'use_server' | 'conflict' => {
			const draft = getLocalDraft();
			if (!draft) return 'use_server';

			const localTime = new Date(draft.lastModified);
			const serverTime = new Date(
				serverData.updatedAt || serverData.createdAt || 0
			);

			const timeDiff = localTime.getTime() - serverTime.getTime();

			if (Math.abs(timeDiff) < 1000) {
				return 'use_server';
			}

			if (timeDiff < -10000) {
				if (draft.isDirty) {
					return 'conflict';
				}
				return 'use_server';
			}

			if (timeDiff > 0) {
				return 'use_local';
			}

			return 'use_server';
		},
		[getLocalDraft]
	);

	useEffect(() => {
		if (!quoteId) return;

		const draft = getLocalDraft();
		if (draft) {
			setIsDirty(draft.isDirty);
			setLastModified(draft.lastModified);
		}
	}, [quoteId, getLocalDraft]);

	return {
		saveLocal,
		getLocalDraft,
		clearLocal,
		isDirty,
		lastModified,
		reconcile,
		markSynced,
	};
};
